import { Col, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import { IOvereniuctuGet, ueOvereniuctuGet, ueOvereniuctuPost } from 'services/specialniSluzby/ueOvereniuctu';
import { IZruseniGet, ueZruseniDelete, ueZruseniGet } from 'services/specialniSluzby/ueZruseni';

import routePathEnum from 'enums/routePathEnum';
import { ETypSeznam } from 'enums/specialniSluzby/typSeznam';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';

import SeznamPozadavkuZaevidovani from './SeznamPozadavkuZaevidovani';

const SeznamPozadavku: React.FC = () => {
    const { aktivniFirma, specialniSluzby, language } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [dataOvereni, setDataOvereni] = useState<IOvereniuctuGet>();
    const [dataZruseni, setDataZruseni] = useState<IZruseniGet>();
    const [pagination, setPagination] = useState<{
        offset: number;
        page: number;
        limit: number;
    }>({
        offset: 0,
        page: 1,
        limit: 10
    });

    const [modalMessage, setModalMessage] = useState<string>('');

    const loadData = (offset?: number) => {
        if (specialniSluzby?.detail && specialniSluzby.seznamTyp === ETypSeznam.OVERENY) {
            setLoading(true);
            ueOvereniuctuGet(specialniSluzby.detail.id!, {
                idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                ucet: specialniSluzby.detailFiltr?.ucet,
                idnabyvatele: specialniSluzby.detailFiltr?.idnabyvatele,
                koducastnika: specialniSluzby.detailFiltr?.koducastnika,
                zdrojdat: specialniSluzby.detailFiltr?.zdrojdat,
                offset: offset !== undefined ? offset : 0,
                limit: pagination.limit
            })
                .then((resp: IOvereniuctuGet) => {
                    setDataOvereni(resp as IOvereniuctuGet);
                })
                .catch((err: any) => {
                    setErrStatus(err.response?.status);
                })
                .finally(() => {
                    setLoading(false);
                    setErrStatus(0);
                });
        } else if (specialniSluzby?.detail && specialniSluzby.seznamTyp === ETypSeznam.SMAZANY) {
            setLoading(true);
            ueZruseniGet(
                specialniSluzby.detail.id!,
                specialniSluzby.pozadavek
                    ? {
                          idpolozky: specialniSluzby.pozadavek?.idpolozky,
                          ucet: specialniSluzby.pozadavek?.ucet,
                          idnabyvatele: specialniSluzby.pozadavek?.idnabyvatele,
                          koducastnika: specialniSluzby.pozadavek?.koducastnika,
                          zdrojdat: specialniSluzby.pozadavek?.zdrojdat,
                          offset: offset !== undefined ? offset : 0,
                          limit: pagination.limit
                      }
                    : {
                          idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                          ucet: specialniSluzby.detailFiltr?.ucet,
                          idnabyvatele: specialniSluzby.detailFiltr?.idnabyvatele,
                          koducastnika: specialniSluzby.detailFiltr?.koducastnika,
                          zdrojdat: specialniSluzby.detailFiltr?.zdrojdat,
                          offset: offset !== undefined ? offset : 0,
                          limit: pagination.limit
                      }
            )
                .then((resp: IZruseniGet) => {
                    setDataZruseni(resp as IZruseniGet);
                })
                .catch((err: any) => {
                    setErrStatus(err.response?.status);
                })
                .finally(() => {
                    setLoading(false);
                    setErrStatus(0);
                });
        }
    };

    const columns = [
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet'
        },
        {
            title: <TranslateText>ID_NABYVATELE</TranslateText>,
            dataIndex: 'idnabyvatele',
            key: 'idnabyvatele'
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        // {
        //     title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
        //     dataIndex: 'koducastnika',
        //     key: 'koducastnika'
        // },
        {
            title: <TranslateText>CISLO_ZASTAVNI_SMLOUVY</TranslateText>,
            dataIndex: 'cislozastavnismlouvy',
            key: 'cislozastavnismlouvy'
        },
        {
            title: <TranslateText>POZNAMKA</TranslateText>,
            dataIndex: 'poznamka',
            key: 'poznamka'
        },
        {
            title: <TranslateText>ZPUSOB_PORIZENI</TranslateText>,
            dataIndex: 'zpusobporizeni',
            key: 'zpusobporizeni',
            render: (text: string) => {
                return <TranslateText>{'SS_DETAIL_TAB_PORIZENI_' + text}</TranslateText>;
            }
        },
        {
            title: <TranslateText>UPOZORNENI</TranslateText>,
            dataIndex: 'upozorneni',
            key: 'upozorneni'
        },
        {
            title: <TranslateText>ID_PREVODU_V_CDCP</TranslateText>,
            dataIndex: 'idvcdcp',
            key: 'idvcdcp'
        },
        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stav',
            key: 'stav',
            render: (text: string) => <TranslateText>{'SS_DETAIL_STAV_' + text}</TranslateText>
        }
    ];

    const columnsSICAV = [
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet'
        },
        {
            title: <TranslateText>ID_NABYVATELE</TranslateText>,
            dataIndex: 'idnabyvatele',
            key: 'idnabyvatele'
        },
        {
            title: <TranslateText>SS_DETAIL_FILTR_TYP</TranslateText>,
            dataIndex: 'typtransakce',
            key: 'typtransakce',
            render: (text: string) =>
                text && (
                    <TranslateText>
                        {text === 'NAKUP' ? 'SS_DETAIL_FILTR_NAKUP' : text === 'PRODEJ' ? 'SS_DETAIL_FILTR_PRODEJ' : ''}
                    </TranslateText>
                )
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },

        // {
        //     title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
        //     dataIndex: 'koducastnika',
        //     key: 'koducastnika'
        // },
        {
            title: <TranslateText>POZNAMKA</TranslateText>,
            dataIndex: 'poznamka',
            key: 'poznamka'
        },
        {
            title: <TranslateText>ZPUSOB_PORIZENI</TranslateText>,
            dataIndex: 'zpusobporizeni',
            key: 'zpusobporizeni',
            render: (text: string) => {
                return <TranslateText>{'SS_DETAIL_TAB_PORIZENI_' + text}</TranslateText>;
            }
        },
        {
            title: <TranslateText>UPOZORNENI</TranslateText>,
            dataIndex: 'upozorneni',
            key: 'upozorneni'
        },
        {
            title: <TranslateText>ID_PREVODU_V_CDCP</TranslateText>,
            dataIndex: 'idvcdcp',
            key: 'idvcdcp'
        },
        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stav',
            key: 'stav',
            render: (text: string) => <TranslateText>{'SS_DETAIL_STAV_' + text}</TranslateText>
        }
    ];

    useEffect(() => {
        specialniSluzby && specialniSluzby.detail?.id && loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specialniSluzby?.detail?.id]);

    const renderModal = (msg: string) => {
        setModalMessage(msg);
    };

    const renderButtons = () => {
        return (
            <Row className="button-row">
                <ButtonComponent
                    id="seznamPozadavkuZpetBtn"
                    type="default"
                    buttontext="ZPET"
                    onClick={() => {
                        navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`);
                    }}
                />
                {specialniSluzby && (
                    <ButtonComponent
                        id="seznamPozadavkuSubmitBtn"
                        buttontext={
                            specialniSluzby.seznamTyp === ETypSeznam.SMAZANY
                                ? 'SMAZAT'
                                : specialniSluzby.seznamTyp === ETypSeznam.OVERENY
                                  ? 'OVERIT'
                                  : 'ZAEVIDOVAT'
                        }
                        onClick={() => {
                            setLoading(true);
                            if (specialniSluzby.seznamTyp === ETypSeznam.OVERENY) {
                                ueOvereniuctuPost(specialniSluzby.detail?.id!, {
                                    idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                                    ucet: specialniSluzby.detailFiltr?.ucet,
                                    idnabyvatele: specialniSluzby.detailFiltr?.idnabyvatele,
                                    koducastnika: specialniSluzby.detailFiltr?.koducastnika,
                                    zdrojdat: specialniSluzby.detailFiltr?.zdrojdat
                                })
                                    .then(() => {
                                        renderModal('SS_SEZNAM_MODAL_USPESNE_' + specialniSluzby.seznamTyp);
                                    })
                                    .catch((err: any) => {
                                        renderModal('RESPONSE_ERROR_' + err.response?.status);
                                    })
                                    .finally(() => {
                                        setLoading(false);
                                    });
                            } else if (specialniSluzby.seznamTyp === ETypSeznam.SMAZANY) {
                                ueZruseniDelete(
                                    specialniSluzby.detail?.id!,
                                    specialniSluzby.pozadavek
                                        ? {
                                              idpolozky: specialniSluzby.pozadavek?.idpolozky,
                                              ucet: specialniSluzby.pozadavek?.ucet,
                                              idnabyvatele: specialniSluzby.pozadavek?.idnabyvatele,
                                              koducastnika: specialniSluzby.pozadavek?.koducastnika,
                                              zdrojdat: specialniSluzby.pozadavek?.zdrojdat
                                          }
                                        : {
                                              idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                                              ucet: specialniSluzby.detailFiltr?.ucet,
                                              idnabyvatele: specialniSluzby.detailFiltr?.idnabyvatele,
                                              koducastnika: specialniSluzby.detailFiltr?.koducastnika,
                                              zdrojdat: specialniSluzby.detailFiltr?.zdrojdat
                                          }
                                )
                                    .then(resp => {
                                        resp.stav?.includes('TIMEOUT')
                                            ? renderModal('SS_SLUZBA_ERROR_NESKORO')
                                            : renderModal('SS_SEZNAM_MODAL_USPESNE_' + specialniSluzby.seznamTyp);
                                    })
                                    .catch((err: any) => {
                                        renderModal('RESPONSE_ERROR_' + err.response?.status);
                                    })
                                    .finally(() => {
                                        setLoading(false);
                                    });
                            }
                        }}
                    />
                )}
            </Row>
        );
    };

    return (
        <>
            {specialniSluzby && specialniSluzby.seznamTyp !== ETypSeznam.ZAEVIDOVANY ? (
                <>
                    <LoadingWrapper loading={loading}>
                        <Title
                            level={4}
                            text={`${
                                getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ]
                            } - ${getLanguageObject(language).ISIN}: ${specialniSluzby?.detail?.isin}, ${
                                getLanguageObject(language).DATUM
                            }: ${dayjs(specialniSluzby?.detail?.datum).format('DD.MM.YYYY')}`}
                        />
                        {errStatus ? (
                            <Row>
                                <Col span={16}>
                                    {errStatus === 404 ? (
                                        <Paragraph text="RESPONSE_ERROR_404" />
                                    ) : errStatus ? (
                                        <Paragraph text="SLUZBA_NEDOSTUPNA" />
                                    ) : null}
                                    <Row className="button-row">
                                        <ButtonComponent
                                            id="seznamPozadavkuErrBtn"
                                            buttontext="ZPET"
                                            onClick={() => {
                                                setErrStatus(0);
                                            }}
                                        />
                                    </Row>
                                </Col>
                            </Row>
                        ) : (
                            <>
                                {specialniSluzby.seznamTyp === ETypSeznam.OVERENY && (
                                    <>
                                        <Title level={5} text="SS_NOVY_SOUBOR_SEZNAM_NADPIS_OVERENI" />
                                        <TableComponent
                                            rowKey={(_, index) => index || 0}
                                            columns={specialniSluzby.detail?.typ === 'SICAV' ? columnsSICAV : columns}
                                            dataSource={dataOvereni?.polozky}
                                            locale={{
                                                emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                                            }}
                                            pagination={{
                                                total: dataOvereni?.count || dataZruseni?.count,
                                                // onChange: page => setTableOffset((page - 1) * 10),
                                                // current: tableOffset / 10 + 1,
                                                onChange: page => {
                                                    setPagination({
                                                        page: page,
                                                        offset: (page - 1) * pagination.limit,
                                                        limit: pagination.limit
                                                    });
                                                    loadData((page - 1) * pagination.limit);
                                                },
                                                current: pagination.page,
                                                showSizeChanger: false
                                            }}
                                        />
                                    </>
                                )}
                                {specialniSluzby.seznamTyp === ETypSeznam.SMAZANY && (
                                    <>
                                        <Title level={5} text="SS_NOVY_SOUBOR_SEZNAM_NADPIS_SMAZANI" />
                                        <TableComponent
                                            rowKey={(_, index) => index || 0}
                                            columns={specialniSluzby.detail?.typ === 'SICAV' ? columnsSICAV : columns}
                                            dataSource={dataZruseni?.polozky}
                                            locale={{
                                                emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                                            }}
                                            pagination={{
                                                total: dataOvereni?.count || dataZruseni?.count,
                                                // onChange: page => setTableOffset((page - 1) * 10),
                                                // current: tableOffset / 10 + 1,
                                                onChange: page => {
                                                    setPagination({
                                                        page: page,
                                                        offset: (page - 1) * pagination.limit,
                                                        limit: pagination.limit
                                                    });
                                                    loadData((page - 1) * pagination.limit);
                                                },
                                                current: pagination.page,
                                                showSizeChanger: false
                                            }}
                                        />
                                    </>
                                )}

                                <Row
                                    style={{
                                        paddingTop: '10px'
                                    }}
                                    gutter={8}
                                >
                                    {(dataOvereni?.pocetcp || dataZruseni?.pocetcp) && (
                                        <Col>
                                            <InputComponent
                                                name="pocetcp"
                                                label="POCET_CP"
                                                notRequired={true}
                                                defaultValue={formatInputNumberThousand(
                                                    dataOvereni?.pocetcp || dataZruseni?.pocetcp || 0
                                                )}
                                                disabled={true}
                                            />
                                        </Col>
                                    )}
                                    {(dataOvereni?.pocetuctu || dataZruseni?.pocetuctu) && (
                                        <Col>
                                            <InputComponent
                                                name="pocetuctu"
                                                label="SS_PREHLED_POCET_UCTU"
                                                notRequired={true}
                                                defaultValue={formatInputNumberThousand(
                                                    dataOvereni?.pocetuctu || dataZruseni?.pocetuctu || 0
                                                )}
                                                disabled={true}
                                            />
                                        </Col>
                                    )}
                                </Row>

                                {renderButtons()}
                            </>
                        )}
                    </LoadingWrapper>
                    <Modal
                        title={
                            <TranslateText>
                                {modalMessage.includes('ERROR_')
                                    ? 'CHYBA'
                                    : specialniSluzby.seznamTyp === ETypSeznam.SMAZANY
                                      ? 'SMAZANI_POZADAVKU'
                                      : specialniSluzby.seznamTyp === ETypSeznam.OVERENY
                                        ? 'SS_OVERENI_UCTU'
                                        : 'SS_ZAEVIDOVAT_V_CDCP'}
                            </TranslateText>
                        }
                        open={!!modalMessage}
                        onCancel={() => {
                            setModalMessage('');
                            !modalMessage.includes('ERROR_') &&
                                navigateWorkaround(
                                    `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`
                                );
                        }}
                        afterClose={() => {
                            setModalMessage('');
                            !modalMessage.includes('ERROR_') &&
                                navigateWorkaround(
                                    `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`
                                );
                        }}
                        footer={null}
                    >
                        <Row>
                            {/* {!modalMessage.includes('ERROR_') && <Paragraph text="SS_SEZNAM_MODAL_USPESNE" />}{' '}
                            <Paragraph text={modalMessage} /> */}
                            <div>
                                {!modalMessage.includes('ERROR_') && (
                                    <TranslateText>
                                        {specialniSluzby.seznamTyp === 'SMAZANY'
                                            ? 'SS_SEZNAM_MODAL_USPESNE_POZADAVKY'
                                            : 'SS_SEZNAM_MODAL_USPESNE'}
                                    </TranslateText>
                                )}{' '}
                                <TranslateText>{modalMessage}</TranslateText>
                            </div>
                        </Row>
                        <Row>
                            <ButtonComponent
                                id="seznamPozadavkuZpetAltBtn"
                                style={{
                                    marginTop: 20
                                }}
                                buttontext="ZAVRIT"
                                type="default"
                                onClick={() => {
                                    setModalMessage('');
                                    !modalMessage.includes('ERROR_') &&
                                        navigateWorkaround(
                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`
                                        );
                                }}
                            />
                        </Row>
                    </Modal>
                </>
            ) : (
                <>
                    {/* Kvůli přehlednosti odděleno do samostatné komponenty - zaevidování má dvě tabulky */}
                    <SeznamPozadavkuZaevidovani />
                </>
            )}
        </>
    );
};

export default SeznamPozadavku;
